import React, { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import useLangNavigate from "../../hooks/useLangNavigate";
import { usePageMeta } from "../../hooks/useMeta";
import {
  LandingHeader,
  LandingFooter,
  ContactFounder,
  LandingCarousel,
  handleImageError,
} from "../../components/landing";
import Button from "../../components/buttons/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faCalendarCheck,
  faGlobe,
  faCashRegister,
  faTablePicnic,
  faMobileScreen,
  faBell,
  faUtensils,
  faPhone,
  faEnvelope,
  faCreditCard,
  faStar,
  faGem,
  faPercent,
} from "@fortawesome/pro-solid-svg-icons";
import { faChevronDown, faArrowRight } from "@fortawesome/pro-light-svg-icons";

// Images
import restoImg from "../../assets/img/landing/resto.webp";
import dashboardProducts from "../../assets/img/landing/reworked/resto_products.webp";
import dashboardSpecials from "../../assets/img/landing/reworked/resto_todaysspecials.webp";
import dashboardDetails from "../../assets/img/landing/reworked/resto_product_details.webp";
import dashboardTablet from "../../assets/img/landing/reworked/resto_tablet.webp";
import stepImg1 from "../../assets/img/landing/reworked/resto_step1.webp";
import stepImg2 from "../../assets/img/landing/reworked/resto_products.webp";
import stepImg3 from "../../assets/img/landing/reworked/resto_step3.webp";

// Partner logos
import logoDaMichele from "../../assets/img/landing/reworked/resto-logo/da-michele-logo.webp";
import logoGyros from "../../assets/img/landing/reworked/resto-logo/gyros-logo.webp";
import logoSanteria from "../../assets/img/landing/reworked/resto-logo/la-santeria-logo.webp";
import logoOptitSushi from "../../assets/img/landing/reworked/resto-logo/optitsushi-logo.webp";
import founderIlirImg from "../../assets/img/landing/reworked/about/founder-1.webp";

function LandingRestaurants() {
  const { t } = useTranslation();
  const navigate = useLangNavigate();

  // Parallax scroll effect
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Floating CTA visibility: show after hero, hide near final CTA
  useEffect(() => {
    const heroHeight = heroRef.current?.offsetHeight || 600;
    const finalCtaTop = finalCtaRef.current?.offsetTop || Infinity;
    const windowHeight = window.innerHeight;

    // Show after scrolling past 80% of hero
    const showThreshold = heroHeight * 0.8;
    // Hide when final CTA is visible (200px buffer)
    const hideThreshold = finalCtaTop - windowHeight + 200;

    setShowFloatingCta(scrollY > showThreshold && scrollY < hideThreshold);
  }, [scrollY]);

  // Dashboard carousel
  const dashboardImages = useMemo(
    () => [
      {
        src: dashboardProducts,
        alt: "Products management",
        description: t("landing.restaurants.carousel.products"),
      },
      {
        src: dashboardSpecials,
        alt: "Today's specials",
        description: t("landing.restaurants.carousel.specials"),
      },
      {
        src: dashboardDetails,
        alt: "Product details",
        description: t("landing.restaurants.carousel.details"),
      },
      {
        src: dashboardTablet,
        alt: "Tablet orders view",
        description: t("landing.restaurants.carousel.tablet"),
      },
    ],
    [t],
  );
  // FAQ accordion state - first item open by default
  const [openFaqIndex, setOpenFaqIndex] = useState(0);
  const [showFloatingCta, setShowFloatingCta] = useState(false);
  const [priceInterval, setPriceInterval] = useState("yearly");

  const finalCtaRef = useRef(null);
  const heroRef = useRef(null);

  const steps = [
    {
      number: 1,
      title: t("landing.restaurants.steps.step1.title"),
      description: t("landing.restaurants.steps.step1.description"),
      duration: t("landing.restaurants.steps.step1.duration"),
      image: stepImg1,
    },
    {
      number: 2,
      title: t("landing.restaurants.steps.step2.title"),
      description: t("landing.restaurants.steps.step2.description"),
      duration: t("landing.restaurants.steps.step2.duration"),
      image: stepImg2,
    },
    {
      number: 3,
      title: t("landing.restaurants.steps.step3.title"),
      description: t("landing.restaurants.steps.step3.description"),
      duration: t("landing.restaurants.steps.step3.duration"),
      image: stepImg3,
    },
  ];

  // FAQ items
  const faqs = useMemo(
    () => [
      {
        question: t("landing.restaurants.faq.cost.question"),
        answer: t("landing.restaurants.faq.cost.answer"),
      },
      {
        question: t("landing.restaurants.faq.hardware.question"),
        answer: t("landing.restaurants.faq.hardware.answer"),
      },
      {
        question: t("landing.restaurants.faq.commitment.question"),
        answer: t("landing.restaurants.faq.commitment.answer"),
      },
      {
        question: t("landing.restaurants.faq.support.question"),
        answer: t("landing.restaurants.faq.support.answer"),
      },
    ],
    [t],
  );

  // Add FAQ structured data (JSON-LD) for SEO
  useEffect(() => {
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "faq-schema";
    script.textContent = JSON.stringify(faqSchema);

    // Remove existing schema if present (for language changes)
    const existing = document.getElementById("faq-schema");
    if (existing) {
      existing.remove();
    }

    document.head.appendChild(script);

    return () => {
      const schemaScript = document.getElementById("faq-schema");
      if (schemaScript) {
        schemaScript.remove();
      }
    };
  }, [faqs]);

  // Roadmap features
  const roadmapFeatures = [
    {
      icon: faCalendarCheck,
      title: t("landing.restaurants.roadmap.reservation.title"),
      description: t("landing.restaurants.roadmap.reservation.description"),
      status: "coming-soon",
      statusLabel: t("landing.restaurants.roadmap.status.comingSoon"),
    },
    {
      icon: faTablePicnic,
      title: t("landing.restaurants.roadmap.tables.title"),
      description: t("landing.restaurants.roadmap.tables.description"),
      status: "coming-soon",
      statusLabel: t("landing.restaurants.roadmap.status.comingSoon"),
    },
    {
      icon: faGlobe,
      title: t("landing.restaurants.roadmap.website.title"),
      description: t("landing.restaurants.roadmap.website.description"),
      status: "coming-soon",
      statusLabel: t("landing.restaurants.roadmap.status.comingSoon"),
    },
    {
      icon: faCashRegister,
      title: t("landing.restaurants.roadmap.pos.title"),
      description: t("landing.restaurants.roadmap.pos.description"),
      status: "coming-soon",
      statusLabel: t("landing.restaurants.roadmap.status.comingSoon"),
    },
  ];

  return (
    <>
      {usePageMeta("landingRestaurants")}
      <div className="landing-restaurant">
        {/* Parallax gradient accents */}
        <div
          className="landing-restaurant__gradient-1"
          style={{ transform: `translateY(${scrollY * 0.15}px)` }}
        />
        <div
          className="landing-restaurant__gradient-2"
          style={{ transform: `translateY(${scrollY * 0.1}px)` }}
        />

        {/* Skip to main content link for accessibility */}
        <a href="#main-content" className="landing-restaurant__skip-link">
          {t("common.skipToContent", "Skip to main content")}
        </a>

        <LandingHeader />

        {/* Hero Section - Dark theme */}
        <section
          ref={heroRef}
          id="main-content"
          className="landing-hero-section landing-restaurant__hero"
        >
          <div className="landing-hero-dots">
            {Array.from({ length: 40 }).map((_, i) => (
              <div
                key={i}
                className={`landing-hero-dot landing-hero-dot--${i % 8}`}
                style={{
                  left: `${5 + ((i * 11) % 90)}%`,
                  top: `${8 + ((i * 13) % 85)}%`,
                  animationDelay: `${i * 0.15}s`,
                }}
                aria-hidden="true"
              />
            ))}
          </div>
          <div className="landing-restaurant__hero-content">
            <div className="landing-restaurant__hero-text">
              {/* Tagline badge */}
              <span className="landing-restaurant__tagline">
                {t("landing.restaurants.hero.tagline")}
              </span>
              <h1 className="landing-restaurant__headline">
                {t("landing.restaurants.hero.headline")}
              </h1>
              <p className="landing-restaurant__subheadline">
                {t("landing.restaurants.hero.subheadline")}
              </p>

              {/* CTAs */}
              <div className="landing-restaurant__cta">
                <Button
                  variant="primary"
                  size="md"
                  className="landing-cta-button--glow landing-restaurant__cta-button--primary"
                  onClick={() => navigate("register")}
                  data-analytics="cta-restaurant-register"
                  data-analytics-location="hero"
                >
                  {t("landing.restaurants.cta.primary")}
                </Button>
                <Button
                  variant="outline"
                  size="md"
                  onClick={() => navigate("demo")}
                  data-analytics="cta-restaurant-demo"
                  data-analytics-location="hero"
                >
                  {t("landing.restaurants.cta.demo")}
                </Button>
              </div>

              {/* Trust items */}
              <div className="landing-restaurant__trust-items">
                <div className="landing-restaurant__trust-item">
                  <FontAwesomeIcon icon={faCheck} />
                  <span>{t("landing.restaurants.hero.trust1")}</span>
                </div>
                <div className="landing-restaurant__trust-item">
                  <FontAwesomeIcon icon={faCheck} />
                  <span>{t("landing.restaurants.hero.trust2")}</span>
                </div>
              </div>
            </div>

            <div className="landing-restaurant__hero-visual">
              <div
                className="landing-floating-accent landing-restaurant__floating-accent"
                aria-hidden="true"
              />
              <LandingCarousel images={dashboardImages} />
            </div>
          </div>

          {/* Stats integrated in hero */}
          <div className="landing-restaurant__stats">
            <div className="landing-restaurant__stat">
              <span className="landing-restaurant__stat-value">
                {t("landing.restaurants.stats.reservations.value")}
              </span>
              <span className="landing-restaurant__stat-label">
                {t("landing.restaurants.stats.reservations.label")}
              </span>
            </div>
            <div className="landing-restaurant__stat">
              <span className="landing-restaurant__stat-value">
                {t("landing.restaurants.stats.basket.value")}
              </span>
              <span className="landing-restaurant__stat-label">
                {t("landing.restaurants.stats.basket.label")}
              </span>
            </div>
            <div className="landing-restaurant__stat">
              <span className="landing-restaurant__stat-value">
                {t("landing.restaurants.stats.service.value")}
              </span>
              <span className="landing-restaurant__stat-label">
                {t("landing.restaurants.stats.service.label")}
              </span>
            </div>
          </div>
        </section>

        {/* Pre-order Flow + Benefits wrapper with shared background */}
        <div
          className="landing-restaurant__value-section"
          style={{ "--parallax-offset": `${scrollY * 0.03}px` }}
        >
          {/* Shared parallax background image */}
          <div
            className="landing-restaurant__value-section-bg"
            style={{
              backgroundImage: `url(${restoImg})`,
              transform: `translateY(${scrollY * 0.05}px)`,
            }}
          />

          {/* Decorative shapes */}
          <div className="landing-restaurant__shape landing-restaurant__shape--1" />
          <div className="landing-restaurant__shape landing-restaurant__shape--2" />

          {/* Pre-order Flow Explainer */}
          <section className="landing-section landing-restaurant__preorder-flow">
            <div className="landing-restaurant__section-container">
              <h2 className="landing-section-title landing-restaurant__section-title">
                {t("landing.restaurants.preorderFlow.title")}
              </h2>
              <p className="landing-section-subtitle landing-restaurant__section-subtitle">
                {t("landing.restaurants.preorderFlow.subtitle")}
              </p>
              <div className="landing-restaurant__preorder-steps">
                <div className="landing-restaurant__preorder-step">
                  <div className="landing-restaurant__preorder-icon">
                    <FontAwesomeIcon icon={faMobileScreen} />
                  </div>
                  <h3>{t("landing.restaurants.preorderFlow.step1.title")}</h3>
                  <p>
                    {t("landing.restaurants.preorderFlow.step1.description")}
                  </p>
                </div>
                <div
                  className="landing-restaurant__preorder-arrow text-danger"
                  aria-hidden="true"
                >
                  →
                </div>
                <div className="landing-restaurant__preorder-step">
                  <div className="landing-restaurant__preorder-icon">
                    <FontAwesomeIcon icon={faBell} />
                  </div>
                  <h3>{t("landing.restaurants.preorderFlow.step2.title")}</h3>
                  <p>
                    {t("landing.restaurants.preorderFlow.step2.description")}
                  </p>
                </div>
                <div
                  className="landing-restaurant__preorder-arrow text-danger"
                  aria-hidden="true"
                >
                  →
                </div>
                <div className="landing-restaurant__preorder-step">
                  <div className="landing-restaurant__preorder-icon">
                    <FontAwesomeIcon icon={faUtensils} />
                  </div>
                  <h3>{t("landing.restaurants.preorderFlow.step3.title")}</h3>
                  <p>
                    {t("landing.restaurants.preorderFlow.step3.description")}
                  </p>
                </div>
              </div>
            </div>
            <div className="landing-restaurant__steps-cta">
              <button
                type="button"
                className="landing-restaurant__steps-cta-link"
                onClick={() => navigate("register")}
                data-analytics="cta-restaurant-register"
                data-analytics-location="steps"
              >
                {t("landing.restaurants.preorderFlow.endphrase")}
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
            </div>
          </section>
        </div>

        {/* How It Works Section with texture */}
        <section
          className="landing-section landing-restaurant__steps"
          style={{ "--parallax-offset": `${scrollY * 0.02}px` }}
        >
          {/* Decorative elements */}
          <div className="landing-restaurant__shape landing-restaurant__shape--3" />

          <div className="landing-restaurant__section-container">
            <h2 className="landing-section-title landing-restaurant__section-title">
              {t("landing.restaurants.steps.title")}
            </h2>

            {/* Progress line connecting steps */}
            <div
              className="landing-restaurant__steps-progress"
              aria-hidden="true"
            />

            <div className="landing-restaurant__steps-grid">
              {steps.map((step) => (
                <div key={step.number} className="landing-restaurant__step-row">
                  <div className="landing-step-card landing-restaurant__step-card">
                    <div className="landing-step-number landing-restaurant__step-number">
                      {step.number}
                    </div>
                    <div className="landing-step-card__content landing-restaurant__step-content">
                      <h3 className="landing-step-card__title landing-restaurant__step-title">
                        {step.title}
                        <span className="landing-restaurant__step-duration">
                          {step.duration}
                        </span>
                      </h3>
                      <p className="landing-step-card__description landing-restaurant__step-description">
                        {step.description}
                      </p>
                    </div>
                  </div>
                  <div className="landing-step-image landing-restaurant__step-image">
                    <img
                      src={step.image}
                      alt={step.title}
                      loading="lazy"
                      onError={handleImageError}
                    />
                  </div>
                </div>
              ))}
            </div>
            {/* Subtle CTA after steps */}
            <div className="landing-restaurant__steps-cta">
              <p className="landing-section-subtitle landing-restaurant__section-subtitle my-2">
                {t("landing.restaurants.steps.endphrase")}
              </p>
              <button
                type="button"
                className="landing-restaurant__steps-cta-link"
                onClick={() => navigate("register")}
                data-analytics="cta-restaurant-register"
                data-analytics-location="steps"
              >
                {t("landing.restaurants.steps.cta")}
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
            </div>
          </div>
        </section>
        {/* Pricing: visible in dev/staging; hidden in production builds */}
        {process.env.NODE_ENV !== "production" && (
          <section className="landing-section landing-restaurant__pricing">
            <div className="landing-restaurant__section-container">
              <p className="landing-section-subtitle landing-restaurant__section-subtitle landing-restaurant__pricing-subtitle">
                {t("landing.restaurants.pricing.subtitle")}
              </p>

              <div className="landing-restaurant__pricing-toggle-wrapper">
                <div className="landing-restaurant__pricing-toggle">
                  <button
                    type="button"
                    className={`landing-restaurant__pricing-toggle-btn${priceInterval === "monthly" ? " landing-restaurant__pricing-toggle-btn--active" : ""}`}
                    onClick={() => setPriceInterval("monthly")}
                  >
                    {t("landing.restaurants.pricing.toggle.monthly")}
                  </button>
                  <button
                    type="button"
                    className={`landing-restaurant__pricing-toggle-btn${priceInterval === "yearly" ? " landing-restaurant__pricing-toggle-btn--active" : ""}`}
                    onClick={() => setPriceInterval("yearly")}
                  >
                    {t("landing.restaurants.pricing.toggle.yearly")}
                    <span className="landing-restaurant__pricing-toggle-badge">
                      {t("landing.restaurants.pricing.toggle.savings")}
                    </span>
                  </button>
                </div>
              </div>

              <div className="landing-restaurant__pricing-grid">
                <div className="landing-restaurant__pricing-card">
                  <div className="landing-restaurant__pricing-card-header">
                    <span className="landing-restaurant__pricing-card-icon">
                      <FontAwesomeIcon icon={faCreditCard} />
                    </span>
                    <h3 className="landing-restaurant__pricing-card-name">
                      {t("landing.restaurants.pricing.plans.essential.name")}
                    </h3>
                  </div>
                  <div className="landing-restaurant__pricing-card-price">
                    <span className="landing-restaurant__pricing-card-amount">
                      {t("landing.restaurants.pricing.plans.essential.price")}
                    </span>
                    <span className="landing-restaurant__pricing-card-currency">
                      CHF
                    </span>
                  </div>
                  <div className="landing-restaurant__pricing-card-commission">
                    <FontAwesomeIcon icon={faPercent} />
                    <span>{t("landing.restaurants.pricing.commission")}</span>
                  </div>
                  <ul className="landing-restaurant__pricing-card-features">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <li key={i}>
                        <FontAwesomeIcon icon={faCheck} />
                        <span>
                          {t(
                            `landing.restaurants.pricing.plans.essential.features.${i}`,
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="landing-restaurant__pricing-card landing-restaurant__pricing-card--popular">
                  <span className="landing-restaurant__pricing-card-badge">
                    {t("landing.restaurants.pricing.plans.gourmet.badge")}
                  </span>
                  <div className="landing-restaurant__pricing-card-header">
                    <span className="landing-restaurant__pricing-card-icon landing-restaurant__pricing-card-icon--popular">
                      <FontAwesomeIcon icon={faStar} />
                    </span>
                    <h3 className="landing-restaurant__pricing-card-name">
                      {t("landing.restaurants.pricing.plans.gourmet.name")}
                    </h3>
                  </div>
                  <div className="landing-restaurant__pricing-card-price">
                    <span className="landing-restaurant__pricing-card-amount">
                      {priceInterval === "yearly"
                        ? t(
                            "landing.restaurants.pricing.plans.gourmet.yearlyPrice",
                          )
                        : t(
                            "landing.restaurants.pricing.plans.gourmet.monthlyPrice",
                          )}
                    </span>
                    <span className="landing-restaurant__pricing-card-currency">
                      CHF
                    </span>
                    <span className="landing-restaurant__pricing-card-period">
                      {priceInterval === "yearly"
                        ? t(
                            "landing.restaurants.pricing.plans.gourmet.yearlyPeriod",
                          )
                        : t("landing.restaurants.pricing.plans.gourmet.period")}
                    </span>
                    <span className="landing-restaurant__pricing-card-daily-chip">
                      {priceInterval === "yearly"
                        ? t(
                            "landing.restaurants.pricing.plans.gourmet.dailyCostYearly",
                          )
                        : t(
                            "landing.restaurants.pricing.plans.gourmet.dailyCostMonthly",
                          )}
                    </span>
                  </div>
                  <div className="landing-restaurant__pricing-card-commission">
                    <FontAwesomeIcon icon={faPercent} />
                    <span>{t("landing.restaurants.pricing.commission")}</span>
                  </div>
                  <ul className="landing-restaurant__pricing-card-features">
                    <li>
                      <FontAwesomeIcon icon={faCheck} />
                      <span>
                        {t(
                          "landing.restaurants.pricing.plans.gourmet.includesIntro",
                        )}
                      </span>
                    </li>
                    {[1, 2, 3, 4].map((i) => (
                      <li key={i}>
                        <FontAwesomeIcon icon={faCheck} />
                        <span>
                          {t(
                            `landing.restaurants.pricing.plans.gourmet.features.${i}`,
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="landing-restaurant__pricing-card">
                  <div className="landing-restaurant__pricing-card-header">
                    <span className="landing-restaurant__pricing-card-icon">
                      <FontAwesomeIcon icon={faGem} />
                    </span>
                    <h3 className="landing-restaurant__pricing-card-name">
                      {t("landing.restaurants.pricing.plans.signature.name")}
                    </h3>
                  </div>
                  <div className="landing-restaurant__pricing-card-price">
                    <span className="landing-restaurant__pricing-card-amount">
                      {priceInterval === "yearly"
                        ? t(
                            "landing.restaurants.pricing.plans.signature.yearlyPrice",
                          )
                        : t(
                            "landing.restaurants.pricing.plans.signature.monthlyPrice",
                          )}
                    </span>
                    <span className="landing-restaurant__pricing-card-currency">
                      CHF
                    </span>
                    <span className="landing-restaurant__pricing-card-period">
                      {priceInterval === "yearly"
                        ? t(
                            "landing.restaurants.pricing.plans.signature.yearlyPeriod",
                          )
                        : t(
                            "landing.restaurants.pricing.plans.signature.period",
                          )}
                    </span>
                    <span className="landing-restaurant__pricing-card-daily-chip">
                      {priceInterval === "yearly"
                        ? t(
                            "landing.restaurants.pricing.plans.signature.dailyCostYearly",
                          )
                        : t(
                            "landing.restaurants.pricing.plans.signature.dailyCostMonthly",
                          )}
                    </span>
                  </div>
                  <div className="landing-restaurant__pricing-card-commission">
                    <FontAwesomeIcon icon={faPercent} />
                    <span>{t("landing.restaurants.pricing.commission")}</span>
                  </div>
                  <ul className="landing-restaurant__pricing-card-features">
                    <li>
                      <FontAwesomeIcon icon={faCheck} />
                      <span>
                        {t(
                          "landing.restaurants.pricing.plans.signature.includesIntro",
                        )}
                      </span>
                    </li>
                    {[1, 2, 3, 4].map((i) => (
                      <li key={i}>
                        <FontAwesomeIcon icon={faCheck} />
                        <span>
                          {t(
                            `landing.restaurants.pricing.plans.signature.features.${i}`,
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="landing-restaurant__pricing-cta">
                <p className="landing-section-subtitle landing-restaurant__section-subtitle my-2">
                  {t("landing.restaurants.pricing.cta.endphrase")}
                </p>
                <button
                  type="button"
                  className="landing-restaurant__steps-cta-link"
                  onClick={() => navigate("register")}
                  data-analytics="cta-restaurant-register"
                  data-analytics-location="pricing"
                >
                  {t("landing.restaurants.pricing.cta.link")}
                  <FontAwesomeIcon icon={faArrowRight} />
                </button>
              </div>
            </div>
          </section>
        )}

        {/* Social Proof - Partner logos */}
        <section className="landing-section landing-restaurant__partners">
          <div className="landing-restaurant__section-container">
            <p className="landing-restaurant__partners-label">
              {t("landing.restaurants.partners.label")}
            </p>
            <div className="landing-restaurant__partners-logos">
              <div className="landing-restaurant__partner-logo">
                <img
                  src={logoDaMichele}
                  alt="Da Michele"
                  loading="lazy"
                  onError={handleImageError}
                />
              </div>
              <div className="landing-restaurant__partner-logo">
                <img
                  src={logoGyros}
                  alt="Gyros"
                  loading="lazy"
                  onError={handleImageError}
                />
              </div>
              <div className="landing-restaurant__partner-logo">
                <img
                  src={logoSanteria}
                  alt="La Santeria"
                  loading="lazy"
                  onError={handleImageError}
                />
              </div>
              <div className="landing-restaurant__partner-logo">
                <img
                  src={logoOptitSushi}
                  alt="Optit Sushi"
                  loading="lazy"
                  onError={handleImageError}
                />
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section - shared .landing-faq* only (_landing-shared.scss) */}
        <section className="landing-section landing-faq landing-restaurant__faq">
          <div className="landing-restaurant__section-container">
            <h2 className="landing-section-title landing-restaurant__section-title">
              {t("landing.restaurants.faq.title")}
            </h2>

            <div className="landing-faq-list">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className={`landing-faq-item ${
                    openFaqIndex === index ? "landing-faq-item--open" : ""
                  }`}
                >
                  <button
                    type="button"
                    className="landing-faq-question"
                    onClick={() =>
                      setOpenFaqIndex(openFaqIndex === index ? null : index)
                    }
                    aria-expanded={openFaqIndex === index}
                  >
                    <span>{faq.question}</span>
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className="landing-faq-icon"
                    />
                  </button>
                  <div className="landing-faq-answer">
                    <p dangerouslySetInnerHTML={{ __html: faq.answer }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Roadmap Section */}
        <section className="landing-section landing-restaurant__roadmap">
          <div className="landing-restaurant__section-container">
            <h2 className="landing-section-title landing-restaurant__section-title">
              {t("landing.restaurants.roadmap.title")}
            </h2>
            <p className="landing-restaurant__roadmap-intro">
              {t("landing.restaurants.roadmap.intro")}
            </p>

            <div className="landing-restaurant__roadmap-grid">
              {roadmapFeatures.map((feature, index) => (
                <div
                  key={index}
                  className={`landing-restaurant__roadmap-card landing-restaurant__roadmap-card--${feature.status}`}
                >
                  <span
                    className={`landing-restaurant__roadmap-status landing-restaurant__roadmap-status--${feature.status}`}
                  >
                    {feature.statusLabel}
                  </span>
                  <div className="landing-restaurant__roadmap-icon">
                    <FontAwesomeIcon icon={feature.icon} />
                  </div>
                  <h3 className="landing-restaurant__roadmap-title">
                    {feature.title}
                  </h3>
                  <p className="landing-restaurant__roadmap-description">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>

            <div className="landing-restaurant__roadmap-cta">
              <a
                href="mailto:info@offood.ch?subject=Feature suggestion"
                className="landing-restaurant__steps-cta-link"
              >
                {t("landing.restaurants.roadmap.suggestion")}
                <FontAwesomeIcon icon={faArrowRight} />
              </a>
            </div>
          </div>
        </section>

        {/* Contact Ilir - before final CTA */}
        <ContactFounder
          sectionTitle={t("landing.contactIlir.sectionTitle")}
          image={founderIlirImg}
          imageAlt={t("landing.contactIlir.name")}
          name={t("landing.contactIlir.name")}
          role={t("landing.contactIlir.role")}
          paragraph={t("landing.contactIlir.paragraph")}
          primaryCta={{
            url: "https://calendly.com/ilir-offood/30min",
            label: t("landing.contactIlir.bookCall"),
            icon: faPhone,
          }}
          secondaryCta={{
            url: "mailto:ilir@offood.ch",
            label: t("landing.contactIlir.emailMe"),
            icon: faEnvelope,
          }}
        />

        {/* Final CTA Section */}
        <section
          ref={finalCtaRef}
          className="landing-section landing-restaurant__final-cta"
        >
          <div className="landing-restaurant__final-cta-container">
            <h2 className="landing-restaurant__cta-headline">
              {t("landing.restaurants.finalCta.headline")}
            </h2>
            <p className="landing-restaurant__cta-subheadline">
              {t("landing.restaurants.finalCta.subheadline")}
            </p>

            <div className="landing-restaurant__cta-buttons">
              <Button
                variant="primary"
                size="md"
                className="landing-cta-button--glow landing-restaurant__cta-button--primary"
                onClick={() => navigate("register")}
                data-analytics="cta-restaurant-register"
                data-analytics-location="footer-cta"
              >
                {t("landing.restaurants.cta.final")}
              </Button>
              <Button
                variant="outline"
                size="md"
                onClick={() => navigate("demo")}
                data-analytics="cta-restaurant-demo"
                data-analytics-location="footer-cta"
              >
                {t("landing.restaurants.cta.demo")}
              </Button>
            </div>

            {/* Swiss trust signal */}
            <div className="landing-restaurant__swiss-trust">
              <svg
                width="20"
                height="20"
                viewBox="0 0 32 32"
                aria-label="Switzerland"
                role="img"
              >
                <rect width="32" height="32" fill="#D52B1E" rx="2" />
                <path d="M13 6h6v7h7v6h-7v7h-6v-7H6v-6h7V6z" fill="#FFF" />
              </svg>
              <span>{t("landing.entry.trust.swissLabel")}</span>
            </div>
          </div>
        </section>

        {/* Banderole CTA - appears after hero, hides near final CTA */}
        {showFloatingCta && (
          <div className="landing-banderole">
            <p className="landing-banderole__text">
              {t("landing.restaurants.banderole.text")}
            </p>
            <div className="landing-banderole__cta">
              <Button
                variant="white"
                size="md"
                onClick={() => navigate("register")}
                data-analytics="cta-restaurant-register"
                data-analytics-location="banderole"
              >
                {t("landing.restaurants.banderole.cta")}
                <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Landing Footer with all links */}
        <LandingFooter />
      </div>
    </>
  );
}

export default LandingRestaurants;
